import axios from "axios";

/**
 * InsuranceDetailsService
 *
 * Service for the IPD-billing Insurance & Claims screen (/ipd-billing/insurance).
 * Follows the singleton pattern of DischargeSummaryService / GenerateAbhaService:
 * one class, one method per endpoint, exported as a single shared instance.
 *
 * Base backend is the primary route URL. ORG-CODE / APP-MODE / USER-ACCESS-KEY /
 * USERNAME headers are injected automatically by the global axios interceptor
 * (src/pages/login/Axios-interceptor.js); callers only pass the Bearer token via
 * `getAuthHeaders()` from ipd-billing/constants.
 *
 * ── BACKEND CONTRACT ──
 *
 *   GET  {BASE}insurance_details/?patient_id=<>&admission_id=<>
 *     Previously-saved eligibility/pre-auth capture (rehydrates the form on reload).
 *     resp: { status, message, is_valid, data: {
 *               patient_id, admission_id,
 *               member_card_number, uhid,
 *               eligibility_check,                 // boolean (true/false)
 *               eligibility_date, eligibility_reference_number,
 *               pre_auth_date, pre_auth_number,
 *               created_at, updated_at
 *           }}
 *
 *   POST {BASE}insurance_details/                   (same URL as the GET, POST verb)
 *     body: { patient_id, admission_id,
 *             member_card_number, uhid,
 *             eligibility_check,                     // boolean (true/false)
 *             eligibility_date, eligibility_reference_number,
 *             pre_auth_date, pre_auth_number }
 *     resp: { status, message }
 *
 *   GET  {BASE}insurance_admission_details/?patient_id=<>&admission_id=<>
 *     Insurance captured at registration (read-only display card).
 *     resp: { status, message, is_valid, data: {
 *               patient_id, admission_id,
 *               insurance_provider, insurance_policy_number, tpa_name,
 *               payer_type,                          // "Insurance" | "Government" | "Corporate"
 *               government_scheme, corporate_name, corporate_reference
 *           }}
 *
 * ── GOTCHA ──
 *   The global interceptor force-logs-out the user on any response with
 *   data.is_valid === false. A patient with no insurance record yet may come back
 *   is_valid:false, so "/insurance_details/" and "/insurance_admission_details/"
 *   are already added to excludedEndpointsForMultipleMachinesLogin in
 *   Axios-interceptor.js.
 */

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

class InsuranceDetailsService {
  // ─── Per-patient insurance details (registration data + saved capture) ──────
  /**
   * Fetch saved eligibility / pre-auth capture for one admission (rehydrates the form).
   *
   * GET {BASE}insurance_details/?patient_id=<>&admission_id=<>
   *
   * @param {{ patientId: string|number, admissionId: string|number }} ids
   * @param {object} headers - Auth headers (Bearer token).
   * @returns {Promise<object>} Server response ({ status, message, is_valid, data }).
   */
  async getInsuranceDetails({ patientId, admissionId }, headers) {
    const endpoint = `${BASE}insurance_details/`;

    const response = await axios.get(endpoint, {
      headers,
      params: {
        patient_id: patientId,
        admission_id: admissionId,
      },
    });

    return response.data;
  }

  // ─── Insurance captured at registration (read-only display card) ────────────
  /**
   * Fetch the insurance details captured for an admission at patient registration
   * (provider / policy / TPA / payer type / government scheme / corporate). Read-only.
   *
   * GET {BASE}insurance_admission_details/?patient_id=<>&admission_id=<>
   *
   * @param {{ patientId: string|number, admissionId: string|number }} ids
   * @param {object} headers - Auth headers (Bearer token).
   * @returns {Promise<object>} Server response ({ status, message, is_valid, data }).
   */
  async getInsuranceAdmissionDetails({ patientId, admissionId }, headers) {
    const endpoint = `${BASE}insurance_admission_details/`;

    const response = await axios.get(endpoint, {
      headers,
      params: {
        patient_id: patientId,
        admission_id: admissionId,
      },
    });

    return response.data;
  }

  // ─── Save eligibility / pre-authorization capture ───────────────────────────
  /**
   * Persist eligibility + pre-auth details for an admission.
   *
   * POST {BASE}insurance_details/  (same URL as getInsuranceDetails, POST verb)
   *
   * @param {object} payload - { patient_id, admission_id, member_card_number, uhid,
   *   eligibility_check (boolean), eligibility_date, eligibility_reference_number,
   *   pre_auth_date, pre_auth_number }
   * @param {object} headers - Auth headers (Bearer token).
   * @returns {Promise<object>} Server response ({ status, message }).
   */
  async saveInsuranceDetails(payload, headers) {
    const endpoint = `${BASE}insurance_details/`;

    const response = await axios.post(endpoint, JSON.stringify(payload), {
      headers: {
        "Content-Type": "application/json",
        ...headers,
      },
    });

    return response.data;
  }

  // ─── Admitted-patient lookup for the on-page selector (reuses ipd_admission/) ─
  /**
   * Fetch admitted patients to populate the patient selector. Reuses the existing
   * IPD admission listing (no new endpoint); the page client-filters by name/UHID.
   *
   * GET {BASE}ipd_admission/?status=Admitted
   *
   * @param {object} headers - Auth headers (Bearer token).
   * @returns {Promise<object>} Server response (admissions under .data / .admission_list).
   */
  async getAdmittedPatients(headers) {
    const endpoint = `${BASE}ipd_admission/`;

    const response = await axios.get(endpoint, {
      headers,
      params: { status: "Allocated" },
    });

    return response.data;
  }
}

const insuranceDetailsServiceInstance = new InsuranceDetailsService();
export default insuranceDetailsServiceInstance;
